import "isomorphic-fetch";
import { Aurelia, PLATFORM } from "aurelia-framework";
import "materialize-css";
import "materialize-css/dist/css/materialize.css";
import { UserManagerSettings, WebStorageStateStore } from "oidc-client";
import { OpenIdConnectConfiguration } from "aurelia-open-id-connect";
import { AureliaApplicationSettings } from "./app/components/models/aurelia-application-settings";
import { Backend, TCustomAttribute } from "aurelia-i18n";
import { HttpClient } from "aurelia-fetch-client";

declare const IS_DEV_BUILD: boolean; // The value is supplied by Webpack during the build


export function configure(aurelia: Aurelia) {

    const applicationSettings = ((window as any).aureliaApplicationSettings as AureliaApplicationSettings);
    aurelia.container.registerInstance(AureliaApplicationSettings, applicationSettings);

    const httpClient = new HttpClient().configure((config: any) => {
        config.withBaseUrl(`${applicationSettings.Authority}/`);
        config.withInterceptor({
            response(response) {
                return response;
            },
            responseError(error: any) {
                return error;
            }
        });
    });

    const container = aurelia.container;
    container.registerInstance(HttpClient, httpClient);


    const oidcConfig = {
        loginRedirectRoute: "/tenants",  // if the user is not authenticated the aurelia router will route you here
        logoutRedirectRoute: "/", // after a successful logout the aurelia router will land you here
        unauthorizedRedirectRoute: "/unauthorized", // if the user is unauthorized you must see this page

        userManagerSettings: {

            // The number of seconds in advance of access token expiry
            // to raise the access token expiring event.
            accessTokenExpiringNotificationTime: 1,

            authority: applicationSettings.Authority,
            automaticSilentRenew: true,

            // IdentityServer4 supports OpenID Connect Session Management
            // https://openid.net/specs/openid-connect-session-1_0.html
            monitorSession: true,
            checkSessionInterval: 2000,

            // The client or application ID that the authority issues.
            client_id: "sanistar-remote-web-app",

            filterProtocolClaims: true,
            loadUserInfo: false,
            post_logout_redirect_uri: `${applicationSettings.ApplicationHost}/signout-oidc`,
            redirect_uri: `${applicationSettings.ApplicationHost}/signin-oidc`,
            response_type: "id_token token",
            scope: "openid api",
            // number of milliseconds to wait for the authorization
            // server to response to silent renew request
            silentRequestTimeout: 10000,
            alwaysIncludeUserClaimsInIdToken: true,
            silent_redirect_uri: `${applicationSettings.ApplicationHost}/signin-oidc`,
            userStore: new WebStorageStateStore({
                prefix: "oidc",
                store: window.localStorage,
            }),
        } as UserManagerSettings,
    } as OpenIdConnectConfiguration;

    aurelia.use
        .standardConfiguration()
        .feature(PLATFORM.moduleName("app/components/attributes/index"))
        .feature(PLATFORM.moduleName("app/components/converters/index"))
        //.feature(PLATFORM.moduleName("app/components/models/index"))
        .feature(PLATFORM.moduleName("app/components/aurelia-chart/index"))
        .plugin(PLATFORM.moduleName("au-table"))
        .plugin(PLATFORM.moduleName("aurelia-materialize-bridge"), b => b.useAll())
        .plugin(PLATFORM.moduleName("aurelia-open-id-connect"), () => oidcConfig)
        .plugin(PLATFORM.moduleName("aurelia-validation"))
        .plugin(PLATFORM.moduleName("aurelia-i18n"),
        
            (instance) => {
                let aliases = ["t", "i18n"];
                // add aliases for 't' attribute
                TCustomAttribute.configureAliases(aliases);

                // register backend plugin
                instance.i18next.use(Backend.with(aurelia.loader));

                var language = localStorage.getItem("user-language");
                if (!language) {
                    language = "en";
                }

                // adapt options to your needs (see http://i18next.com/docs/options/)
                // make sure to return the promise of the setup method, in order to guarantee proper loading
                return instance.setup({
                    //backend: { loadPath: '../locales/{{lng}}/{{ns}}.json' },
                    attributes: aliases,
                    lng: language,
                    fallbackLng: "en",
                    debug: true,
                    resources: {
                        en: {
                            translation: {
                                "actions": "Actions",
                                "add-tenant": "Add Tenant",
                                "add-customer": "Add Customer",
                                "add-existing-user": "Add Existing User",
                                "add-user": "Add User",
                                "add-kiosk": "Add Kiosk",
                                "admin": "Admin",
                                "admin-dashboard": "Admin Dashboard",
                                "administration-options": "Administration Options",
                                "auto-confirm": "Automatically Confirm User",
                                "cancel": "Cancel",
                                "card-holder": "Card Holder",
                                "card-number": "Card #",
                                "close": "Close",
                                "cloud-sync": "Cloud Sync",
                                "cloud-sync-settings": "Cloud Sync Settings",
                                "container-name": "Container Name",
                                "copy-kiosk-id": "Copy Kiosk ID to Clipboard",
                                "copy-client-secret": "Copy Client Secret to Clipboard",
                                "copy-transaction-id": "Copy Transaction ID to Clipboard",
                                "created": "Created",
                                "customer-code": "Customer Code",
                                "customer-name": "Customer Name",
                                "customer-settings": "Customer Settings",
                                "customers": "Customers",
                                "days-since-last-sync-threshold": "Days since last sync threshhold",
                                "days-since-last-sync-message": "Enter the number of days to wait until a reminder email is sent. The default is 14 days.",
                                "declined-batch-transactions": "Declined Batch Transactions",
                                "delayed-kiosk-cloud-sync": "Delayed Kiosk Cloud Sync",
                                "delete-user": "Delete User",
                                "delete-user-confirm": "Do you want to delete this User?",
                                "delete-user-global-admin-msg": "This user is a GlobalAdministrator. Deleting will remove this user from this Customer only, without changing user's access to the system. To disable this user, select CANCEL to close this dialog without deleting this user. Open the Manage User dialog, uncheck the Global Administrator check box, and save changes. Then proceed to Delete User.",
                                "description": "Description",
                                "email": "Email",
                                "email-preferences": "Email Preferences",
                                "first-name": "First Name",
                                "get-ticket-template-names-from-workflow": "Get Ticket Template Names from Workflow",
                                "generate-password": "Generate Password",
                                "generate-client-secret": "Generate Client Secret",
                                "global-admin": "Global Admin",
                                "global-settings": "Global Settings",
                                "global-email-recipient": "Global Email Recipient",
                                "global-email-recipient-comment": "If checked, the user will received any of the email preferences checked above for the kiosks from all of the customers.",
                                "headed-kiosk": "Headed Kiosk",
                                "home": "Home",
                                "home-message": "Please choose an option below:",
                                "id": "ID",
                                "kiosk-id": "Kiosk ID",
                                "kiosk-id-reversed": "Reversed Kiosk ID",
                                "kiosk-information": "Kiosk Information",
                                "kiosk-logs": "Kiosk Logs",
                                "kiosk-type": "Kiosk Type",
                                "kiosk": "Kiosk",
                                "kiosks": "Kiosks",
                                "kiosk-has-modem": "The kiosk has a modem installed",
                                "kiosk-measurements": "Kiosk Measurements",
                                "kiosk-send-health-email": "Send kiosk health emails?",
                                "kiosk-reset-ticket-stock-amount": "Reset Ticket Stock Amount",
                                "kiosk-reminder-days": "Reminder Days",
                                "kiosk-send-reminder-email": "Send periodic reminder emails?",
                                "kiosk-settings": "Kiosk Settings",
                                "misc-kiosk-settings": "Miscellaneous Kiosk Settings",
                                "kiosk-client-secret": "Client Secret",
                                "kiosk-has-client-secret": "Has secret",
                                "kiosk-upload-log-files": "Upload log files on next sync",
                                "last-four": "Last Four",
                                "last-name": "Last Name",
                                "last-sync": "Last Cloud Sync",
                                "login": "Login",
                                "logout": "Logout",
                                "lookup": "Transaction Lookup",
                                "low-battery-voltage": "Low Battery Voltage",
                                "low-battery-voltage-threshold": "Low Battery Voltage Threshold",
                                "low-ticket-stock": "Low Ticket Stock",
                                "low-ticket-stock-threshold": "Low Ticket Stock Threshold",
                                "machine-login": "Machine Login?",
                                "magensa-settings": "Magensa Settings",
                                "magensa-url": "Magensa URL",
                                "manage-kiosk": "Manage Kiosk",
                                "manage-kiosks": "Manage Kiosks",
                                "manage-processing": "Manage Processing",
                                "manage-refunds": "Manage Refunds",
                                "manage-transactions": "Manage Transactions",
                                "manage-tenant": "Manage Tenant",
                                "manage-user": "Manage User",
                                "manage-users": "Manage Users",
                                "merchant-processor": "Merchant Processor",
                                "modified": "Updated",
                                "name": "Name",
                                "no": "No",
                                "no-results": "No Results Found",
                                "no-disable": "No - Disable",
                                "ok": "OK",
                                "password": "Password",
                                "payment-settings": "Payment Settings",
                                "phone-number": "Phone Number",
                                "please-copy-client-secret": "Make sure to copy the generated client secret as you will not be able to see it again!",
                                "processor-name": "Processor Name",
                                "process-refund": "Process Refund",
                                "process-transaction": "Process Transaction",
                                "process-transactions": "Process Transactions?",
                                "process-transactions-global": "Global Process Transactions",
                                "processed": "Processed",
                                "refund": "Refund",
                                "reports": "Reports",
                                "resubmit-transactions": "Resubmit Declined Transactions",
                                "searching": "Searching...",
                                "selected": "Selected",
                                "select-all": "Select All",
                                "select-kiosks": "Select Kiosks",
                                "select-kiosk": "Select a kiosk",
                                "select-kiosk-type": "Select a kiosk type",
                                "send-batch-transact-process-email": "Send batch transaction processing email",
                                "search-for-user": "Search for User",
                                "ssid": "SSID",
                                "status": "Status",
                                "sub-domain": "Subdomain",
                                "tenants": "Tenants",
                                "tenant-admin": "Tenant Admin",
                                "ticket-templates": "Ticket Templates",
                                "ticket-stock": "Ticket Stock",
                                "timezone": "Time Zone",
                                "type": "Type",
                                "transactions": "Transactions",
                                "transaction-amount": "Amount",
                                "transaction-batch": "Transaction Batch",
                                "transaction-details": "Transaction Details",
                                "transaction-status": "Transaction Status",
                                "transaction-timestamp": "Date",
                                "unauthorized": "Unauthorized",
                                "unauthorized-message": "You are not authorized to access Sani-Star REMOTE.  Please contact support to set up an account.",
                                "user-info": "User Information",
                                "username": "Username",
                                "users": "Users",
                                "user-settings": "User Settings",
                                "users-lookup-placeholder": "Lookup by first name, last name or email address",
                                "users-and-kiosks": "Users & Kiosks",
                                "webapp": "Web App",
                                "welcome-message": "Welcome to Sani-Star REMOTE",
                                "wifi-direct": "WiFi Direct",
                                "yes": "Yes",
                                "yes-delete-user": "Yes - Delete User",
                                "yes-enable": "Yes - Enable",
                                "workflow": "Workflow"
                            }
                        }
                    }
                });
            });
     

	if (IS_DEV_BUILD) {
		aurelia.use.developmentLogging();
	}

	aurelia.start().then(() => aurelia.setRoot("app/components/app/app"));
}
